import {Component, OnInit, ViewChild} from '@angular/core';
import {FormControl, FormGroup} from "@angular/forms";
import {ApplicationAnalyticsService} from "./application-analytics.service";
import {MessageService} from "primeng/api";
import {Platform} from "@angular/cdk/platform";
import {environment} from "../../environments/environment";
import {DatePipe} from "@angular/common";
import * as FileSaver from 'file-saver';
import {AdminControlService} from "../@shared/services/admin-control.service";
import {CacheService} from "../@shared/services/cache.service";
import {Paginator} from "primeng/paginator";

@Component({
    selector: 'app-application-analytics',
    templateUrl: './application-analytics.component.html',
    styleUrls: ['./application-analytics.component.scss']
})
export class ApplicationAnalyticsComponent implements OnInit {

    analyticsFilterForm: FormGroup;
    fromDateTime = new Date();
    applicationLogs: any;
    userActions: any[] = [];
    isLoading: boolean;
    showFilterOptions = false;
    emailAndPageTitleData: any;
    environment: string;
    envOptions: any[] = [
        {envDisplay: 'All', envValue: ''},
        {envDisplay: 'QA', envValue: 'qa'},
        {envDisplay: 'PROD', envValue: 'prod'},
    ];
    pageTitles: any;
    isExecuted = false;
    businessEntities: any[] = [];
    domains: any[] = [];

    @ViewChild('logsPaginator') logsPaginator: Paginator | any;
    isDownloading = false;

    constructor(
        private appAnalyticsService: ApplicationAnalyticsService,
        private messageService: MessageService,
        public platform: Platform,
        private adminService: AdminControlService,
        private cacheService: CacheService
    ) {
    }

    ngOnInit(): void {
        this.environment = environment.env;
        this.createAnalyticsForm();
        if (environment.env !== 'dev') {
            this.businessEntities = this.cacheService?.retrieveParsedResponse('business-entities');
            this.fromDateTime.setDate(this.fromDateTime.getDate() - 30);
            this.createAnalyticsForm();
            this.analyticsFilterForm?.controls?.fromLogDateTime.setValue(this.fromDateTime);
            this.analyticsFilterForm?.controls?.toLogDateTime.setValue(new Date());
            this.getPageTitles();
            if (this.businessEntities?.length === 0) {
                this.getBusinessEntities();
            }
            this.getDomains();
        }
    }

    getFilteredApplicationLogs() {
        this.isExecuted = true;
        const filterModelVO = this.analyticsFilterForm.value;
        this.getApplicationLogsService(filterModelVO, 0, 10);
        this.logsPaginator?.changePage(0);
    }

    getApplicationLogsService(filterModelVO: any, page: number = 0, size: number = 10) {
        this.applicationLogs = [];
        this.isLoading = true;
        this.appAnalyticsService.getFilteredApplicationRoute(filterModelVO, page, size)
            .subscribe((x) => {
                this.isLoading = false;
                this.applicationLogs = x;
            }, error => {
                this.isLoading = false;
                this.messageService.add({
                    summary: 'Application Analytics Error !',
                    severity: 'error',
                    detail: error?.message ? error?.message : error,
                    key: 'app-analytics-error',
                    life: 3000
                });
            });
    }

    getUserActions() {
        this.appAnalyticsService.getUserActions()
            .subscribe((x) => {
                this.userActions = x;
            }, error => {
                this.messageService.add({
                    summary: 'Application Analytics Error !',
                    severity: 'error',
                    detail: error?.message ? error?.message : error,
                    key: 'app-analytics-error',
                    life: 3000
                });
            });
    }

    getPageTitles() {
        this.appAnalyticsService
            .getPageTitles()
            .subscribe((x) => {
                this.pageTitles = x;
            }, error => {
                this.messageService.add({
                    summary: 'Page Titles error !',
                    severity: 'error',
                    detail: error?.message ? error?.message : error,
                    key: 'app-analytics-error',
                    life: 3000
                });
            });
    }

    getBusinessEntities() {
        this.adminService.getBusinessEntityListing()
            .subscribe((x) => {
                this.cacheService.cacheResponse('business-entities', x);
                this.businessEntities = this.cacheService.retrieveParsedResponse('business-entities');
            });
    }

    getDomains() {
        this.adminService.getDomainVOList()
            .subscribe((x) => {
                this.domains = x;
            });
    }

    exportExcel() {
        this.isDownloading = true;
        this.appAnalyticsService.downloadApplicationLogs(this.analyticsFilterForm.value)
            .subscribe((res) => {
                const exportedBookings = this.exportedColumnsForFileDownload(res);
                import('xlsx').then(xlsx => {
                    const worksheet = xlsx.utils.json_to_sheet(exportedBookings);
                    const workbook = {Sheets: {data: worksheet}, SheetNames: ['data']};
                    const excelBuffer: any = xlsx.write(workbook, {bookType: 'xlsx', type: 'array'});
                    this.saveAsExcelFile(excelBuffer, 'web-app-logs');
                    this.isDownloading = false;
                });
            }, error => {
                this.messageService
                    .add({
                        summary: 'Download Error ',
                        key: 'app-analytics-error',
                        detail: error?.message ? error?.message : error,
                        severity: 'success'
                    });
                this.isDownloading = false;
            });

    }

    saveAsExcelFile(buffer: any, fileName: string): void {
        const EXCEL_TYPE = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8';
        const EXCEL_EXTENSION = '.xlsx';
        const data: Blob = new Blob([buffer], {
            type: EXCEL_TYPE
        });
        FileSaver.saveAs(data, fileName + '_export_' + new Date().getTime() + EXCEL_EXTENSION);
    }

    exportedColumnsForFileDownload(dataContent: any) {
        const datePipe = new DatePipe('en-US');
        const exportLogs = dataContent?.map((log) => {
            const exportLog = {
                'Email Id': log?.emailId ? log?.emailId : '-',
                'First Name': log?.firstName ? log?.firstName : '-',
                'Last Name': log?.lastName ? log?.lastName : '-',
                'Log Date': log?.logDateTime ? datePipe.transform(log?.logDateTime, 'dd-MMM-yyyy h:mm a') : '-',
                'Customer Name': log?.userBusinessEntities?.length === 0 ? '-' : log?.userBusinessEntities?.map((x) => {
                    return x?.entityName;
                }).join(','),
                'Customer Id': log?.userBusinessEntities?.length === 0 ? '-' : log?.userBusinessEntities?.map((x) => {
                    return x?.entityId;
                }).join(','),
                Domain: log?.userDomains?.length === 0 ? 'All Domains Assigned' :
                    log?.userDomains?.length === 18 ? 'All Domains Assigned' : log?.userDomains?.map((x) => {
                        return x?.domainName + '( ' + x?.domainId + ' )';
                    }).join(','),
                'User Location': log?.region ? log?.region + ' ' + log?.countryCode : '-',
                'Page Title': log?.pageTitle ? log?.pageTitle : '-',
                'Page Url': log?.pageView ? log?.pageView : '-',
            };
            return exportLog;
        });
        console.log('Exported Logs : ', exportLogs);
        return exportLogs;
    }

    createAnalyticsForm() {
        this.analyticsFilterForm = new FormGroup({
            fromLogDateTime: new FormControl(),
            toLogDateTime: new FormControl(),
            pageTitle: new FormControl(),
            businessEntity: new FormControl(),
            domain: new FormControl(),
            environment: new FormControl('')
        });
    }

    onPaginate(event: any) {
        this.isExecuted = true;
        this.getApplicationLogsService(this.analyticsFilterForm.value, event?.page, 10);
    }
}
